import React, { useState, useEffect } from "react";
import { InputGroup, InputRightElement } from "@chakra-ui/react";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
} from "@chakra-ui/react";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT } from "constants/brandColors";

const USERS_PAGE_ID = PAGE_IDS.USERS;
const ROLES_PAGE_ID = PAGE_IDS.ROLES;

function CreateUser({ onCreated }) {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [roleId, setRoleId] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await fetchWithToken(
          `${process.env.REACT_APP_NEWS_API_URL}/role?page_id=${ROLES_PAGE_ID}`
        );
        if (response) {
          setRoles(response.data || []);
        } else {
          console.error("Error fetching roles");
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };
    fetchRoles();
  }, []);

  // Server requires: uppercase letter, digit, symbol, 7-64 chars.
  const validatePassword = (password) => {
    const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])[\s\S]{7,64}$/;
    return regex.test(password);
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Same validation rules as before, just reported per-field instead of a
  // single message so the form can show inline FormErrorMessage text.
  const validateForm = () => {
    const errors = {};
    if (!firstName.trim()) {
      errors.firstName = t("First name is required");
    }
    if (!lastName.trim()) {
      errors.lastName = t("Last name is required");
    }
    if (!email.trim()) {
      errors.email = t("Email is required");
    } else if (!validateEmail(email)) {
      errors.email = t("Please enter a valid email address");
    }
    if (!password) {
      errors.password = t("Password is required");
    } else if (!validatePassword(password)) {
      errors.password = t("Password must be 7-64 characters with at least one uppercase letter, one number, and one special character");
    }
    if (!roleId) {
      errors.roleId = t("Role selection is required");
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const errors = validateForm();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const data = {
        page_id: USERS_PAGE_ID,
        firstName,
        lastName,
        email,
        password,
        roleId: parseInt(roleId, 10),
      };
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/createUser`,
        "POST",
        data
      );
      // fetchWithToken already throws on a non-2xx response, so getting here
      // means the request succeeded — don't also gate on a `status: "success"`
      // field, since the actual response body doesn't reliably have one.
      if (response) {
        // Reset the form and let the parent list refresh itself (via
        // onCreated) instead of doing a full page reload.
        setFirstName("");
        setLastName("");
        setEmail("");
        setPassword("");
        setRoleId("");
        setFieldErrors({});
        onCreated?.();
      } else {
        setError(t("Failed to create user"));
      }
    } catch (error) {
      console.error("Error creating user:", error);
      setError(error.message || t("Failed to create user. Please try again later."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormControl>
      <form onSubmit={handleSubmit}>
        <FormControl isInvalid={!!fieldErrors.firstName} mb="16px">
          <FormLabel fontSize="sm" fontWeight="500" mb="8px" color={GOLD_TEXT}>
            {t("First Name")}
          </FormLabel>
          <Input
            isRequired
            type="text"
            placeholder={t("First Name")}
            size="lg"
            bg="white"
            borderColor={GOLD_BORDER}
            focusBorderColor={GOLD_BORDER}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <FormErrorMessage>{fieldErrors.firstName}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!fieldErrors.lastName} mb="16px">
          <FormLabel fontSize="sm" fontWeight="500" mb="8px" color={GOLD_TEXT}>
            {t("Last Name")}
          </FormLabel>
          <Input
            isRequired
            type="text"
            placeholder={t("Last Name")}
            size="lg"
            bg="white"
            borderColor={GOLD_BORDER}
            focusBorderColor={GOLD_BORDER}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          <FormErrorMessage>{fieldErrors.lastName}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!fieldErrors.email} mb="16px">
          <FormLabel fontSize="sm" fontWeight="500" mb="8px" color={GOLD_TEXT}>
            {t("Email")}
          </FormLabel>
          <Input
            isRequired
            type="email"
            placeholder={t("Email")}
            size="lg"
            bg="white"
            borderColor={GOLD_BORDER}
            focusBorderColor={GOLD_BORDER}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <FormErrorMessage>{fieldErrors.email}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!fieldErrors.password} mb="16px">
          <FormLabel fontSize="sm" fontWeight="500" mb="8px" color={GOLD_TEXT}>
            {t("Password")}
          </FormLabel>
          <InputGroup size="lg">
            <Input
              isRequired
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              bg="white"
              borderColor={GOLD_BORDER}
              focusBorderColor={GOLD_BORDER}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              pr="40px"
            />
            <InputRightElement width="3rem" display="flex" alignItems="center" h="100%">
              <Button
                h="1.75rem"
                size="sm"
                variant="ghost"
                onClick={() => setShowPassword((show) => !show)}
                tabIndex={-1}
              >
                {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
              </Button>
            </InputRightElement>
          </InputGroup>
          <FormErrorMessage>{fieldErrors.password}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!fieldErrors.roleId} mb="16px">
          <FormLabel fontSize="sm" fontWeight="500" mb="8px" color={GOLD_TEXT}>
            {t("Role")}
          </FormLabel>
          <Select
            isRequired
            placeholder={t("Select Role")}
            size="lg"
            bg="white"
            borderColor={GOLD_BORDER}
            focusBorderColor={GOLD_BORDER}
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
          >
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.roleName}
              </option>
            ))}
          </Select>
          <FormErrorMessage>{fieldErrors.roleId}</FormErrorMessage>
        </FormControl>

        <Button
          type="submit"
          fontSize="sm"
          bg={GOLD_BORDER}
          color="#1A1A1A"
          _hover={{ bg: GOLD_TEXT, color: "white" }}
          _active={{ bg: GOLD_TEXT, color: "white" }}
          fontWeight="500"
          w="100%"
          h="50px"
          mb="16px"
          isLoading={isSubmitting}
        >
          {t("Create User")}
        </Button>
        {error && (
          <Box color="red.500" mt={2} whiteSpace="pre-line">
            {Array.isArray(error)
              ? error.map((line, i) => <div key={i}>{line}</div>)
              : <div>{error}</div>
            }
          </Box>
        )}
      </form>
    </FormControl>
  );
}

export default CreateUser;
